import { DatePickerPlacements, StandardDatePicker } from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import classnames from 'classnames';
import React, { FunctionComponent, useLayoutEffect, useRef } from 'react';

import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import { StandardTextField } from '@components/library/text-field';
import { getStandardDatePickerLabels } from '@translations';

import classes from './api-key-form-fields.module.scss';
import {
  ApiKeyFormValues,
  DESCRIPTION_MAX_LENGTH,
  KEY_NAME_MAX_LENGTH,
  toExpiresAt,
} from './apiKeyForm';

interface ApiKeyFormFieldsProps {
  /** Disables + greys the Description field (managed rotate inherits it). */
  descriptionDisabled?: boolean;
  /** Prefix for the field element ids so multiple modals stay unique. */
  idPrefix: string;
  /** Disables + greys the Key Name field (managed rotate inherits it). */
  nameDisabled?: boolean;
  nameError?: string;
  onDescriptionChange: (description: string) => void;
  /** Receives the API-formatted expiry string (see toExpiresAt). */
  onExpiryChange: (expiresAt: string) => void;
  onNameChange: (name: string) => void;
  values: ApiKeyFormValues;
}

/**
 * The shared Key Name / Description / Expiry fields used by both the Create and
 * Rotate API key modals. Name/Description can be individually disabled so the
 * managed rotate flow can show the inherited values read-only while still letting
 * the user pick a new expiry.
 */
export const ApiKeyFormFields: FunctionComponent<ApiKeyFormFieldsProps> = ({
  descriptionDisabled = false,
  idPrefix,
  nameDisabled = false,
  nameError = '',
  onDescriptionChange,
  onExpiryChange,
  onNameChange,
  values,
}) => {
  const expiryInputRef = useRef<HTMLInputElement>(null);
  const expiryPlaceholder = t`Expires in 5 years by default`;

  // StandardDatePicker hardcodes locale placeholders (e.g. mm/dd/yyyy); override for this field.
  useLayoutEffect(() => {
    const input = expiryInputRef.current;
    if (!input) return undefined;

    const applyPlaceholder = (): void => {
      if (input.getAttribute('placeholder') !== expiryPlaceholder) {
        input.setAttribute('placeholder', expiryPlaceholder);
      }
    };

    applyPlaceholder();
    const observer = new MutationObserver(applyPlaceholder);
    observer.observe(input, { attributes: true, attributeFilter: ['placeholder'] });

    return (): void => observer.disconnect();
  }, [expiryPlaceholder]);

  return (
    <>
      <StandardTextField
        className={classnames(classes.textField, { [classes.textFieldReadonly]: nameDisabled })}
        disabled={nameDisabled}
        error={nameError}
        id={`${idPrefix}-name`}
        label={<Trans>Key Name</Trans>}
        maxLength={KEY_NAME_MAX_LENGTH}
        name="name"
        onChange={(e): void => onNameChange(e.target.value)}
        placeholder={t`Input Key Name`}
        required
        value={values.name}
      />

      <StandardTextField
        className={classnames(classes.textField, { [classes.textFieldReadonly]: descriptionDisabled })}
        disabled={descriptionDisabled}
        id={`${idPrefix}-description`}
        label={<Trans>Description</Trans>}
        maxLength={DESCRIPTION_MAX_LENGTH}
        name="description"
        onChange={(e): void => onDescriptionChange(e.target.value)}
        placeholder={t`Optional`}
        value={values.description}
      />

      <div className={classes.expiryDateField}>
        <StandardDatePicker
          ref={expiryInputRef}
          allowPastDates={false}
          id={`${idPrefix}-expiry-date`}
          labels={getStandardDatePickerLabels({
            chooseDate: expiryPlaceholder,
            label: t`Expiry Date`,
          })}
          locale={determineUGTLocale()}
          onSelection={(date): void => onExpiryChange(toExpiresAt(date))}
          showLabel
          useLayerOptions={{
            placement: DatePickerPlacements.BottomStart,
          }}
        />
      </div>
    </>
  );
};
